"use strict";

var Logger = require("js-logger");
var LoggerInstance = require("js-logger").get("CheckoutStore");

var CheckoutConstants = require("./CheckoutConstants");

var ActionsHelper = require("../actions/ActionsHelper");

var WizardConstants = require("../constants/WizardConstants");

var Constants = require("../designer/Constants");

var Dispatcher = require("../dispatcher/Dispatcher");

var HtmlHelper = require("../shared/HtmlHelper");
var NumberHelper = require("../shared/NumberHelper");

var AbstractStore = require("../stores/AbstractStore");

var state = {
	allowedShippingMethods: null,
	availableQuantity: null,
	cartType: "CURRENT",
	checkoutType: null,
	company: null,
	couponInitialized: false, // Used to make sure we only try initializing an OPCO coupon once per page load.
	defaultDonationUpsellLabel: null,
	defaultDonationUpsellAmountLabel: null,
	defaultShippingLabel: null,
	defaultSubtotalLabel: null,
	defaultShippingMethod: null,
	defaultTaxLabel: null,
	defaultTotalLabel: null,
	donations: [],
	donationSubtotal: null,
	donationAmount: null, // Will be set when the project donation amount changes in the project store.
	donationFrequency: null, // Will be set when the project donation frequency changes in the project store.
	donationUpsellAmount: null,
	error: [],	//Used for Shopping Cart page.
	errorsToPreventCheckoutExist: false,	//Used for Shopping Cart page.
	eventRegistrations: [],
	eventRegistrationSubtotal: null,
	freeShippingMethod: null,
	hasShippableProducts: null,
	hasOnlyDownloadableOrFreeShippingProducts: null,
	isDesignMode: false,
	isDonationChargeToday: false, // Will be set when the project frequency, use on day, recurring upsell, or recurring start date changes in the project store
	isPayPalSupported: false,
	isInPledgeStatementSession: false,
	isShoppingCart: false,
	loggedInAccount: null,
	payPalActions: { enable: () => {}, disable: () => {} },
	payPalEnabled: true,
	productCode: null, // Will only be set when on a Product page
	productDiscount: null, // Will only be set when on a Product page. Returned from applyOnePageCoupon API call.
	productPrice: null, // Will only be set when on a Product page. Will be either sales price or retail price if there is no sales price.  Used for shipping and tax now.  Price of the main Product on the Product page.
	products: [],
	productSubtotal: null,
	saveForLaterDonations: [],
	saveForLaterRegistrations: [],
	saveForLaterProducts: [],
	saveForLaterSubscriptions: [],
	shippingAddress: null,
	shippingAmount: null,
	shippingMethod: null,
	shippingRates: null,
	subaccountAmounts: {},
	subscriptions: [],
	subscriptionSubtotal: null,
	taxAmount: null,
	warehouse: null,
	wizardApplePayConfiguration: {},
	wizardApplePayDisplayMessages: {},
	wizardCheckoutDisplayMessages: {},
	wizardCompanyEftErrorMessages: {},
	wizardCompanyErrorMessages: {},
	wizardCouponDisplayMessages: {},
	wizardCreditCardDisplayMessages: {},
	wizardDefaultContactMethod: {},
	wizardDefaultUseOnDay: {},
	wizardDonationUpsell: {},
	wizardFrequencies: {},
	wizardGooglePayConfiguration: {},
	wizardGooglePayDisplayMessages: {},
	wizardUseOnDays: {},
	wizardDonationRequired: false,
	wizardOneTimePasswordConfiguration: {},
	wizardOneTimePasswordForGuestCheckoutMessages: {},
	wizardPayPalDisplayMessages: {},
	wizardProjectCompletionDefaults: {},
	wizardRecurringRequired: false,
	wizardSavedPaymentsDisplayMessages: {},
	wizardShippingDisplayMessages: {}
};

var SHIPPING_EVENT = "shippingChange";

var CheckoutStore = class extends AbstractStore {
	//Specific event for shipping
	addShippingListener(callback) {
		this.on(SHIPPING_EVENT, callback);
	}

	removeShippingListener(callback) {
		this.removeListener(SHIPPING_EVENT, callback);
	}

	emitShippingEvent() {
		this.emit(SHIPPING_EVENT);
	}

	getState() {
		return state;
	}

	get(key) {
		return state[key];
	}

	couponHasBeenInitialized() {
		state.couponInitialized = true;
	}

	getCartType() {
		return state.cartType;
	}

	getCartDonationForUpdate(cartItemId, options) {
		return this.getCartItemForUpdate(cartItemId, Constants.CART_ITEM_PROJECT, options);
	}

	getCartProductForUpdate(cartItemId) {
		return this.getCartItemForUpdate(cartItemId, Constants.CART_ITEM_PRODUCT);
	}

	getCartDonations() {
		return [].concat(state.donations);
	}

	getSaveForLaterCartDonations() {
		return [].concat(state.saveForLaterDonations);
	}

	getCartProducts() {
		return [].concat(state.products);
	}

	getCartSubscriptions() {
		return [].concat(state.subscriptions);
	}

	getCartItemForUpdate(cartItemId, itemType, options) {
		options = options || {};
		var cartItem = this.getItem(cartItemId, itemType);

		// getItem handles logging for !cartItem
		if (!cartItem)
			return;

		if (options.removePremium || options.newStartDate) {
			cartItem.isDirty = true;
		}

		if (cartItem.isDirty) {
			return cartItem;
		}
	}

	getDonationAmount(cartItemId) {
		return this.getAmount(cartItemId, Constants.CART_ITEM_PROJECT);
	}

	getGiftGroupDonationsByCartItemId(cartItemId) {
		var cartItem = this.getItem(cartItemId, Constants.CART_ITEM_PROJECT);
		if (cartItem && cartItem.groupId) {
			return state.donations.filter(elem => elem.groupId && (elem.groupId === cartItem.groupId));
		}

		return [];
	}

	getGiftGroupDonationsByGroupId(groupId) {
		if (groupId) {
			return state.donations.filter(elem => elem.groupId === groupId);
		}

		return [];
	}

	getDonationGroupTotal(cartItemId, options) {
		options = options || {};

		// get the other donations with the same groupId
		var cartItem = this.getItem(cartItemId, Constants.CART_ITEM_PROJECT);
		var groupDonations = this.getGiftGroupDonationsByCartItemId(cartItemId);

		// Get what the total would be if we removed this donation
		if (options.isRemoving) {
			groupDonations = groupDonations.filter(elem => elem.id !== cartItem.id);
		}

		return groupDonations.reduce((donationSum, donation) => donationSum + NumberHelper.toNumber(donation.amount), 0);
	}

	getMinimumDonationAmount(cartItemId) {
		return this.getMinimumAmount(cartItemId, Constants.CART_ITEM_PROJECT);
	}

	getMinimumAmount(cartItemId, itemType) {
		var cartItem = this.getItem(cartItemId, itemType);
		if (cartItem) {
			return cartItem.minimumDonationAmount;
		}
		return null;
	}

	getAmount(cartItemId, itemType) {
		var cartItem = this.getItem(cartItemId, itemType);
		if (cartItem) {
			return cartItem.amount;
		}
		return null;
	}

	getDonationChargeToday(cartItemId) {
		return this.getChargeToday(cartItemId, Constants.CART_ITEM_PROJECT);
	}

	getChargeToday(cartItemId, itemType) {
		var cartItem = this.getItem(cartItemId, itemType);
		if (cartItem) {
			return cartItem.customerChoseChargeToday;
		}
		return null;
	}

	getDonationFrequency(cartItemId) {
		return this.getFrequency(cartItemId, Constants.CART_ITEM_PROJECT);
	}

	getFrequency(cartItemId, itemType) {
		var cartItem = this.getItem(cartItemId, itemType);
		if (cartItem) {
			return cartItem.frequency;
		}
		return null;
	}

	getDonationPremium(cartItemId) {
		return this.getPremium(cartItemId, Constants.CART_ITEM_PROJECT);
	}

	getDonationStartDate(cartItemId) {
		var cartItem = this.getItem(cartItemId, Constants.CART_ITEM_PROJECT);
		if (cartItem) {
			var donationStartDate = cartItem.startDate;
			var donationUseOnDay = cartItem.useOnDay;

			var startDate = donationStartDate
				? moment(donationStartDate, "YYYY-MM-DD")
				: moment();
			startDate.date(donationUseOnDay);

			return startDate;
		}
		//Essentially, you would only get in here if we are in the designer; otherwise, there should be a cart item.
		return moment();
	}

	getPremium(cartItemId, itemType) {
		var cartItem = this.getItem(cartItemId, itemType);
		if (cartItem) {
			return cartItem.premium;
		}
		return null;
	}

	getProductQuantity(cartItemId) {
		return this.getQuantity(cartItemId, Constants.CART_ITEM_PRODUCT);
	}

	getQuantity(cartItemId, itemType) {
		var cartItem = this.getItem(cartItemId, itemType);
		if (cartItem) {
			return cartItem.quantity;
		}
		return null;
	}

	getDonationUseOnDay(cartItemId) {
		return this.getUseOnDay(cartItemId, Constants.CART_ITEM_PROJECT);
	}

	getIsDonationChargeTodayRequired(cartItemId) {
		return state.wizardProjectCompletionDefaults.requireDonation === WizardConstants.PROJECT_COMPLETION_DEFAULTS_REQUIRE_DONATION_VALUES.YES ||
			(
				this.getDonationPremium(cartItemId) &&
				state.wizardProjectCompletionDefaults.requireDonation === WizardConstants.PROJECT_COMPLETION_DEFAULTS_REQUIRE_DONATION_VALUES.WITH_PREMIUM
			);
	}

	getRequireDonationChangedMessage() {
		return state.wizardProjectCompletionDefaults.requireDonationChangedMessage;
	}

	getShippingAmount() {
		var rate;
		if (state.shippingMethod && state.shippingRates && state.shippingRates.length) {
			rate = state.shippingRates.find(s => s.id === state.shippingMethod && !s.isError);
		}

		if (state.hasOnlyDownloadableOrFreeShippingProducts) {
			return 0;
		}

		// Instead of overwriting the amount in the shippingRates array, if this is
		// the freeShippingMethod we should return 0 here.
		return (rate || {}).id === state.freeShippingMethod ? 0 : (rate || {}).amount;
	}

	getShippingMethodsForQuote() {
		var shippingMethods = state.allowedShippingMethods || [];

		if (state.defaultShippingMethod && !shippingMethods.find(method => method === state.defaultShippingMethod)) {
			shippingMethods.push(state.defaultShippingMethod);
		}
		if (state.freeShippingMethod && !shippingMethods.find(method => method === state.freeShippingMethod)) {
			shippingMethods.push(state.freeShippingMethod);
		}

		return shippingMethods;
	}

	getUseOnDay(cartItemId, itemType) {
		var cartItem = this.getItem(cartItemId, itemType);
		if (cartItem) {
			return cartItem.useOnDay;
		}
		return null;
	}

	getItem(cartItemId, itemType) {
		var cartItem;
		switch (itemType) {
			case Constants.CART_ITEM_EVENT:
				cartItem = state.eventRegistrations.find(d => d.id == cartItemId); // coercion to allow "123" to compare correctly to 123
				if (!cartItem && !state.isDesignMode) {
					cartItem = state.saveForLaterRegistrations.find(d => d.id == cartItemId); //coercion to allow "123" to compare correctly to 123
					if (cartItem) {
						cartItem.cartType = "SFL";
					} else {
						LoggerInstance.error(`Could not find event registration. Id = ${cartItemId}`);
					}
				}
				break;
			case Constants.CART_ITEM_PROJECT:
				cartItem = state.donations.find(d => d.id == cartItemId); // coercion to allow "123" to compare correctly to 123
				if (!cartItem && !state.isDesignMode) {
					cartItem = state.saveForLaterDonations.find(d => d.id == cartItemId); //coercion to allow "123" to compare correctly to 123
					if (cartItem) {
						cartItem.cartType = "SFL";
					} else {
						LoggerInstance.error(`Could not find donation. Id = ${cartItemId}`);
					}
				}
				break;
			case Constants.CART_ITEM_PRODUCT:
				cartItem = state.products.find(d => d.id == cartItemId); // coercion to allow "123" to compare correctly to 123
				if (!cartItem && !state.isDesignMode) {
					cartItem = state.saveForLaterProducts.find(d => d.id == cartItemId); // coercion to allow "123" to compare correctly to 123
					if (cartItem) {
						cartItem.cartType = "SFL";
					} else {
						LoggerInstance.error(`Could not find product. Id = ${cartItemId}`);
					}
				}
				break;
			case Constants.CART_ITEM_SUBSCRIPTION:
				cartItem = state.subscriptions.find(d => d.id == cartItemId);
				if (!cartItem && !state.isDesignMode) {
					cartItem = state.saveForLaterSubscriptions.find(d => d.id == cartItemId); //coercion to allow "123" to compare correctly to 123
					if (cartItem) {
						cartItem.cartType = "SFL";
					} else {
						LoggerInstance.error(`Could not find subscriptions. Id = ${cartItemId}`);
					}
				}
				break;
			default:
				LoggerInstance.error(`itemType not recognized for getItem. itemType = ${itemType}`);
				break;
		}

		return cartItem;
	}

	isShoppingCart() {
		return state.isShoppingCart;
	}

	getProductSubtotal() {
		if (state.isShoppingCart) {
			return null;
		}

		var quantity = Number($("input.main-product-quantity").val()) || 1;
		var discount = Number(state.productDiscount) || 0;
		var price = Number(state.productPrice) || 0;
		var subtotal = (price * quantity) - discount;

		return subtotal;
	}

	getChargeTodayProjectSubtotalForOPCO() {
		//If we are not charging today, the subtotal is 0
		if (!state.isDonationChargeToday) {
			return 0;
		}

		return this.getProjectDonationTotalForOPCO();
	}

	getProjectDonationTotalForOPCO() {
		var subaccountTotal = Object.keys(state.subaccountAmounts).reduce((sum, key) => sum + parseFloat(Number(state.subaccountAmounts[key]) || 0), 0);
		var donationAmount = Number(state.donationAmount) || 0;
		return donationAmount + subaccountTotal;
	}

	getPledgeSubtotal() {
		var shoppingCartData = window.PledgeActions.getShoppingCartData() || { pledges: [] };
		var subtotal = 0;

		shoppingCartData.pledges.forEach(pledge => {
			if (pledge.donateTodaySelected || (pledge.isRecurring && pledge.upsell)) {
				subtotal += Number(pledge.amount) || 0;
			}
		});

		return subtotal;
	}

	getSubtotal() {
		var checkoutTypeInformation = this.getCheckoutTypeInformation();

		var subtotal = null;
		if (checkoutTypeInformation.isProductOnePageCheckout) {
			// This is for the product one-page checkout scenario, where we need to adjust for tax and shipping.
			subtotal = this.getProductSubtotal();
		} else if (checkoutTypeInformation.isProjectOnePageCheckout) {
			subtotal = this.getChargeTodayProjectSubtotalForOPCO();
		} else if (checkoutTypeInformation.isPledgeOnePageCheckout) {
			subtotal = this.getPledgeSubtotal();
		} else {
			var productSubtotal = Number(state.productSubtotal) || 0;
			var donationSubtotal = Number(state.donationSubtotal) || 0;
			var eventRegistrationSubtotal = Number(state.eventRegistrationSubtotal) || 0;
			var subscriptionSubtotal = Number(state.subscriptionSubtotal) || 0;
			subtotal = productSubtotal + donationSubtotal + eventRegistrationSubtotal + subscriptionSubtotal;
		}
		return subtotal;
	}

	getCheckoutTypeInformation() {
		var isProductOnePageCheckout = state.productCode && state.productPrice;

		var isProjectOnePageCheckout = !state.isShoppingCart && (
			state.checkoutType === Constants.TANGIBLE_TYPES.PROJECTS ||
			state.checkoutType === Constants.TANGIBLE_TYPES.SECURE_PROJECTS
		);

		var isPledgeOnePageCheckout = !state.isShoppingCart && (
			state.checkoutType === Constants.TANGIBLE_TYPES.PLEDGES ||
			state.checkoutType === Constants.TANGIBLE_TYPES.CHILD ||
			state.checkoutType === Constants.TANGIBLE_TYPES.SECURE_PLEDGES
		);

		var isMultiPageCheckout = !isProductOnePageCheckout && !isProjectOnePageCheckout && !isPledgeOnePageCheckout;

		return {
			isProductOnePageCheckout,
			isProjectOnePageCheckout,
			isPledgeOnePageCheckout,
			isMultiPageCheckout
		};
	}

	setOPCOSubtotal() {
		if (state.isDesignMode || state.isShoppingCart) {
			return;
		}

		var $subtotalElement = $("[data-dynamic='Subtotal']");
		if ($subtotalElement.length) {
			var subtotal = ActionsHelper.formatAmount(this.getSubtotal(), $subtotalElement);

			if (subtotal == null) { //coercion because I want undefined as well but not 0 for tax Amount
				subtotal = state.defaultSubtotalLabel || "";
			}
			$subtotalElement.html(subtotal);
		}
		this.setTotalAmount();
	}

	setShippingAmount() {
		var $shippingElement = $("[data-dynamic='Shipping']");
		if (!$shippingElement.length) {
			return;
		}

		var value = ActionsHelper.formatAmount(this.getShippingAmount(), $shippingElement);

		if (value == null) { //coercion because I want undefined as well but not 0 for shipping Amount
			value = state.defaultShippingLabel || "";
		}

		$shippingElement.html(value);
		this.setTaxAmount(); //This is mainly for the cause when I am clearing out shipping, I may want tax to be cleared as well
		this.setTotalAmount();
	}

	initializeShippingMethodRates() {
		if (state.isDesignMode) {
			return;
		}

		// Initialize all radio buttons as unchecked and disabled.
		$("input[name=selectedShippingMethod]").prop("checked", false).prop("disabled", true);
	}

	isPageType(pageTypeConstant) {
		return this.get("checkoutType") === pageTypeConstant;
	}

	// This function will return true if we require a shipping method, but we don't have one.
	getIsShippingMethodRequiredAndMissing() {
		var $shippingMethods;

		if (state.hasShippableProducts && !state.hasOnlyDownloadableOrFreeShippingProducts && !state.shippingMethod) {
			// - If no rates are available, or all rates are in error, we will be unable to process an order.
			if (!state.shippingRates || state.shippingRates.every(rate => rate.isError === true)) {
				return {
					isMissing: true,
					errorMessage: this.getWizardValue(
						WizardConstants.WIZARDS.SHIPPING_MESSAGES,
						WizardConstants.SHIPPING_MESSAGE_FIELDS.SHIPPING_INCALCULABLE
					)
				};
			}

			// If we cannot select a shipping method because there is no Shipping Method component on the page, we depend on the default shipping method.
			$shippingMethods = $("[data-component='shippingMethods']");
			if (!$shippingMethods.length) {
				var isDefaultShippingValid = state.shippingRates.find(rate => rate.id === state.defaultShippingMethod && !rate.isError);

				return {
					isMissing: !isDefaultShippingValid,
					errorMessage: !isDefaultShippingValid && this.getWizardValue(
						WizardConstants.WIZARDS.SHIPPING_MESSAGES,
						WizardConstants.SHIPPING_MESSAGE_FIELDS.SHIPPING_INCALCULABLE
					)
				};
			}

			// If there are valid rates available but none are selected, we initially select one.
			selectInitialShippingMethod();

			// There is still no shipping method selected.
			if (!state.shippingMethod) {
				return {
					isMissing: true,
					errorMessage: this.getWizardValue(
						WizardConstants.WIZARDS.SHIPPING_MESSAGES,
						WizardConstants.SHIPPING_MESSAGE_FIELDS.SHIPPING_METHOD_MISSING
					)
				};
			}
		}

		return {
			isMissing: false
		};
	}

	setShippingMethodRates() {
		// deselect and disable all shipping method radio buttons.
		$("input[name=selectedShippingMethod]").prop("checked", false).prop("disabled", true);
		$(`[data-dynamic='shippingMethodRate']`).text(this.getWizardValue(
			WizardConstants.WIZARDS.SHIPPING_MESSAGES,
			WizardConstants.SHIPPING_MESSAGE_FIELDS.SHIPPING_METHOD_NOT_AVAILABLE
		));

		// If there are no valid shipping rates, unset existing shipping method.
		// This will force the API to use the default shipping method.
		// Because this is called by the coupon processing, if we've not yet finished
		// looking up shipping rates, bail here and once we're done with the lookup
		// everything should update properly.
		if (!state.shippingRates || state.shippingRates.every(rate => rate.isError === true)) {
			state.shippingMethod = null;
			return;
		}

		if (state.freeShippingMethod && state.shippingRates.every(rate => rate.id !== state.freeShippingMethod)) {
			// push the coupon shipping rate.
			state.shippingRates.push({
				addedForCoupon: true,
				amount: 0,
				description: this.getWizardValue(
					WizardConstants.WIZARDS.SHIPPING_MESSAGES,
					WizardConstants.SHIPPING_MESSAGE_FIELDS.SHIPPING_METHOD_FREE
				),
				errorMessage: null,
				id: state.freeShippingMethod,
				isError: false
			});
		}

		state.shippingRates.forEach(rate => {
			var shippingMethod = rate.id;
			var shippingRate = rate.amount;
			var isError = rate.isError;

			var $eachShipRate = $(`[data-dynamic='shippingMethodRate'][data-shipping-method='${shippingMethod}']`);

			if (isError) {
				return;
			}

			if (shippingRate === 0 || shippingMethod === state.freeShippingMethod) {
				$eachShipRate.text(this.getWizardValue(
					WizardConstants.WIZARDS.SHIPPING_MESSAGES,
					WizardConstants.SHIPPING_MESSAGE_FIELDS.SHIPPING_METHOD_FREE
				));
			} else {
				var value = ActionsHelper.formatAmount(shippingRate, $eachShipRate);
				$eachShipRate.text(value);
			}

			$(`input[name=selectedShippingMethod][value=${shippingMethod}]`).prop("disabled", false);
		});

		this.selectInitialShippingMethod();
	}

	selectInitialShippingMethod() {
		if (!($("[data-component='shippingMethods']").length) && state.shippingRates.find(rate => rate.id === state.defaultShippingMethod && !rate.isError)) {
			state.shippingMethod = state.defaultShippingMethod;
		} else if (state.freeShippingMethod && state.shippingRates.find(rate => rate.id === state.freeShippingMethod)) {
			$(`input[name=selectedShippingMethod][value=${state.freeShippingMethod}]`).prop("checked", true);
			state.shippingMethod = state.freeShippingMethod;
		} else if (state.shippingRates.find(rate => rate.id === state.defaultShippingMethod && !rate.isError)) {
			$(`input[name=selectedShippingMethod][value=${state.defaultShippingMethod}]`).prop("checked", true);
			state.shippingMethod = state.defaultShippingMethod;
		} else {
			$("input[name=selectedShippingMethod]:not(:disabled)").first().prop("checked", true);
			state.shippingMethod = $("input[name=selectedShippingMethod]:not(:disabled)").first().val();
		}
	}

	setFreeShippingMethod(shippingMethod, shippingMethodDescription) {
		var $exists = $(`input[name=selectedShippingMethod][value=${shippingMethod}]`);
		if (!$exists.length) {
			// create a copy of the first available one
			var $firstContainer = $("[data-component='shippingMethods'] .shipping-methods-items-wrapper div[class='row']").first().find("div[class^='col-']").first();
			var $clonedContainer = $firstContainer.clone(/*withDataAndEvents*/true);

			// update all Ids in this component
			$clonedContainer.find("[id]").each((index, item) => {
				$(item).attr("id", HtmlHelper.getId($("body")));
			});

			// update data-driven properties
			$clonedContainer.find("[data-component='shippingMethods.selector']").attr("value", shippingMethod);
			$clonedContainer.find("[data-dynamic='Model.GetShippingMethodDescription(shippingMethodItem.ToString())']").text(shippingMethodDescription);
			$clonedContainer.find("[data-dynamic='shippingMethodRate']").text(this.getWizardValue(
				WizardConstants.WIZARDS.SHIPPING_MESSAGES,
				WizardConstants.SHIPPING_MESSAGE_FIELDS.SHIPPING_METHOD_FREE
			)).attr("data-shipping-method", shippingMethod);

			// add it to the last row
			HtmlHelper.addColumnToLastOrNewRow($clonedContainer, $("[data-component='shippingMethods'] .shipping-methods-items-wrapper"));
		}
	}

	// Return value indicates if the free shipping method was selected.
	// This is used to determine post-remove if we need to update shipping methods.
	removeFreeShippingMethod() {
		// Check to see if we HAVE a free shipping method.
		if (state.freeShippingMethod) {
			// If so, we need to check to make sure it didn't already exist before the coupon was applied
			var freeRate = state.shippingRates.find(rate => {
				return rate.id === state.freeShippingMethod;
		});

			if (freeRate && freeRate.addedForCoupon) {
				// remove the column from the page
				var $freeShippingComponent = $(`input[name=selectedShippingMethod][value=${state.freeShippingMethod}]`);
				var $freeShippingColumn = $freeShippingComponent
					.closest("[data-component='shippingMethods.shippingMethod']") // get the wrapper
					.closest("div[class^='col-']"); // get the column that contains the wrapper
				HtmlHelper.deleteChildOfColumnAndPromoteColumnChildren($freeShippingColumn);

				state.shippingRates.splice(state.shippingRates.findIndex(r => r.id === freeRate.id), 1);
			}
		}

		return state.freeShippingMethod === state.shippingMethod;
	}

	setTaxAmount() {
		var $taxElement = $("[data-dynamic='Tax']");
		if (!$taxElement.length) {
			return;
		}

		var value = ActionsHelper.formatAmount(state.taxAmount, $taxElement);

		if (value == null) { //coercion because I want undefined as well but not 0 for tax Amount
			value = state.defaultTaxLabel || "";
		}

		$taxElement.html(value);
		this.setTotalAmount();
	}

	setDonationUpsellAmount() {
		var $donationUpsellAmountElement = $("[data-dynamic='DonationUpsell']");
		if ($donationUpsellAmountElement.length) {
			var value = ActionsHelper.formatAmount(state.donationUpsellAmount, $donationUpsellAmountElement);

			if (value == null) { //coercion because I want undefined as well but not 0 for donation upsell
				value = state.defaultDonationUpsellAmountLabel || "";
			}

			$donationUpsellAmountElement.html(value);
		}
		this.setTotalAmount();
	}

	setRecurringDonationUpsellAmounts() {
		if (this.isShoppingCart()) {
			var $recurringDonations = $("[data-recurring-frequency]");
			$recurringDonations.each((index, recurringDonation) => {
				var $recurringDonation = $(recurringDonation);
				var originalAmount = Number($recurringDonation.attr("data-recurring-original-amount"));
				var recurringDonationUpsellAmount = Number($recurringDonation.attr("upsell-amount"));
				var totalAmount = originalAmount + recurringDonationUpsellAmount;
				$recurringDonation.html(ActionsHelper.formatAmount(totalAmount, $recurringDonation));
			});
		} else {
			var $recurringDonation = $("[data-dynamic='AmountForFrequency']");
			var recurringUpsellAmount = Number($recurringDonation.attr("upsell-amount")) || 0;
			var totalAmount = this.getProjectDonationTotalForOPCO() + recurringUpsellAmount;

			$("[data-dynamic='AmountForFrequency']").html(ActionsHelper.formatAmount(totalAmount, $recurringDonation));
		}
	}

	getTotalAmount() {
		var subtotal = this.getSubtotal() || 0;
		var shipping = Number(this.getShippingAmount()) || 0;
		var tax = Number(state.taxAmount) || 0;
		var donationUpsellAmount = Number(state.donationUpsellAmount) || 0;

		return NumberHelper.roundToTwoDecimals(subtotal + shipping + tax + donationUpsellAmount);
	}

	setTotalAmount() {
		this.updatePaymentComponents();

		var $totalElement = $("[data-dynamic='Total']");
		if (!$totalElement.length) {
			return;
		} else {
			var total = ActionsHelper.formatAmount(this.getTotalAmount(), $totalElement);
			if (total == null) { //coercion because I want undefined as well but not 0 for total
				total = state.defaultTotalLabel || "";
			}

			$totalElement.html(total);
		}

	}

	setHasNewPaymentBeenShown() {
		state.hasNewPaymentBeenShown = true;
	}

	setDonationUpsellWizard(wizardDonationUpsell) {
		state.wizardDonationUpsell = wizardDonationUpsell;
	}

	hasAutoRenewSubscriptions() {
		return state.subscriptions.some(subscription => {
			return subscription.autoRenew == true;
		});
	}

	hasRecurringDonations() {
		if (state.checkoutType === Constants.TANGIBLE_TYPES.PROJECTS || state.checkoutType === Constants.TANGIBLE_TYPES.SECURE_PROJECTS) {
			//Project One Page Checkout Frequency
			return state.donationFrequency !== "O";
		}

		return state.donations.some((donation, index) => {
			return donation.isRecurring === true;
		});
	}

	/**
	 * This method determines or proxies to the actions class to determine if we need to show or hide
	 * the payment information on shopping cart or OPCO. If we're on a tangible page and the
	 * shouldHidePaymentInformation method is not defined, it is presumably not needed (ProjectActions
	 * will never have a $0 transaction so we should never hide payment information.
	 */
	updatePaymentComponents() {
		if (!state.isDesignMode) {
			var $savedPayments = $("[data-component='savedPayments']");
			var $createAccountAndCheckout = $("[data-component='createAccountAndCheckout']");
			var $oneTimeOnlyPaymentComponents = $("[data-component='payPalCheckout'], [data-component='applePay'], [data-component='googlePay']");
			var shouldHidePaymentInfo = false;

			if (state.isShoppingCart || state.checkoutType === Constants.TANGIBLE_TYPES.PRODUCTS) {
				var hasRecurringDonations = this.hasRecurringDonations();
				var hasAutoRenewSubscriptions = this.hasAutoRenewSubscriptions();
				shouldHidePaymentInfo = {
					hideAllPayments: this.getTotalAmount() === 0 && !hasRecurringDonations && !hasAutoRenewSubscriptions,
					hideOneTimeOnlyPayments: hasRecurringDonations || hasAutoRenewSubscriptions
				};
			} else {
				// get tangibleActions
				var tangibleActions = this.getTangibleActions();

				if (tangibleActions && _.isFunction(tangibleActions.shouldHidePaymentInformation)) {
					shouldHidePaymentInfo = tangibleActions.shouldHidePaymentInformation();
				}
			}

			if (shouldHidePaymentInfo.hideAllPayments) {
				this.hidePaymentComponents($savedPayments, $createAccountAndCheckout, $oneTimeOnlyPaymentComponents);
			} else if (shouldHidePaymentInfo.hideOneTimeOnlyPayments) {
				this.hidePaymentComponents(null, null, $oneTimeOnlyPaymentComponents);
				//We are not supposed to hide all so go ahead and show savedPayments and createAccountAndCheckout
				this.showPaymentComponents($savedPayments, $createAccountAndCheckout, null);

				$("[data-component='paymentType.savePayment.input']").each((index, element) => {
					var $element = $(element);
					if ($element.data("originalCheckedValue") == null) {  //coercion to match null and undefined
						//Only set the originalCheckedValue if it has not been set before.
						$element.data("originalCheckedValue", $element.prop("checked"));
					}

					//If there are any paymentType.savePayment.input components, we need to disable them and check them by default
					$element.prop("disabled", true).prop("checked", true);
				});
			}
			else {
				this.showPaymentComponents($savedPayments, $createAccountAndCheckout, $oneTimeOnlyPaymentComponents);

				$("[data-component='paymentType.savePayment.input']").each((index, element) => {
					var $element = $(element);
					//If there are any paymentType.savePayment.input components, they no longer need to be disabled because we do not have any recurring payments being made.
					$element.prop("disabled", false);

					if ($element.data("originalCheckedValue") != null) {  //coercion to match null and undefined
						$element.prop("checked", $element.data("originalCheckedValue"));
						$element.data("originalCheckedValue", null);
					}
				});
			}
		}
	}

	hidePaymentComponents($savedPaymentComponent, $createAccountAndCheckoutComponent, $oneTimeOnlyPaymentComponents) {
		if ($savedPaymentComponent && $savedPaymentComponent.length) {
			$savedPaymentComponent.find("[data-component='savedPayments.creditCard']").hide();
			$savedPaymentComponent.find("[data-component='savedPayments.eft']").hide();
			$savedPaymentComponent.find("[data-component='savedPayments.addPrompt']").hide();
			$savedPaymentComponent.find("[data-component='savedPayments.addNew']").hide();
		}

		if ($createAccountAndCheckoutComponent && $createAccountAndCheckoutComponent.length) {
			$createAccountAndCheckoutComponent.find("[data-component='paymentType']").hide();
		}

		if ($oneTimeOnlyPaymentComponents && $oneTimeOnlyPaymentComponents.length) {
			$oneTimeOnlyPaymentComponents.hide();
		}
	}

	showPaymentComponents($savedPaymentComponent, $createAccountAndCheckoutComponent, $oneTimeOnlyPaymentComponents) {
		if ($savedPaymentComponent && $savedPaymentComponent.length) {
			var $creditCardsAndEfts = $savedPaymentComponent.find("[data-component='savedPayments.creditCard'], [data-component='savedPayments.eft']");
			$creditCardsAndEfts.show();
			if ($creditCardsAndEfts.length && !state.hasNewPaymentBeenShown) {
				$savedPaymentComponent.find("[data-component='savedPayments.addPrompt']").show();
			} else {
				var $addNewPaymentComponent = $savedPaymentComponent.find("[data-component='savedPayments.addNew']");
				$addNewPaymentComponent.show();
			}
		}

		if ($createAccountAndCheckoutComponent && $createAccountAndCheckoutComponent.length) {
			$createAccountAndCheckoutComponent.find("[data-component='paymentType']").show();
		}

		if ($oneTimeOnlyPaymentComponents && $oneTimeOnlyPaymentComponents.length) {
			$oneTimeOnlyPaymentComponents.show();
		}
	}

	/**
	 * This method sets a callback method in tangible action classes so that they can let this store know
	 * that we need to try and update the UI to show/hide payment information (no $ for OPCO transaction).
	 *
	 * The action class should have a method setChangeHandlerMethod that takes in our callback to be
	 * triggered at the appropriate time.
	 *
	 * If no method is defined on the actions class, we do not attempt to set it as presumably it's not
	 * needed (ex: ProjectActions will never have a $0 transaction).
	 */
	initializeTangibleChangeHandler() {
		if (!state.isDesignMode && !state.isShoppingCart) {
			var tangibleActions = this.getTangibleActions();
			if (_.isFunction(tangibleActions.setChangeHandlerMethod)) {
				tangibleActions.setChangeHandlerMethod(this.updatePaymentComponents.bind(this));
			} else {
				LoggerInstance.debug(`Function setChangeHandlerMethod not defined in ${state.checkoutType} action class `)
			}
		}
	}

	getTangibleActions() {
		var checkoutType = state.checkoutType;

		switch (checkoutType) {
			case Constants.TANGIBLE_TYPES.PRODUCTS:
				return window.ProductActions;
			case Constants.TANGIBLE_TYPES.PROJECTS:
			case Constants.TANGIBLE_TYPES.SECURE_PROJECTS:
				return window.ProjectActions;
			case Constants.TANGIBLE_TYPES.PLEDGES:
			case Constants.TANGIBLE_TYPES.CHILD:
			case Constants.TANGIBLE_TYPES.SECURE_PLEDGES:
				return window.PledgeActions;
		}

		return null;
	}

	setPayPalActions(actions, enabledOnLoad) {
		// intentional coersion for checking both values.
		if (actions != null) {
			this.payPalActions = actions;
		}
		this.payPalEnabled = enabledOnLoad != null ? enabledOnLoad : true;

		var payPalErrorMessages = this.getPayPalErrorMessages();
		if (this.payPalEnabled && !payPalErrorMessages) {
			this.enablePayPal();
		} else {
			LoggerInstance.debug("We've initialized PayPal state as disabled.");
			this.disablePayPal(payPalErrorMessages);
		}
	}

	enablePayPal() {
		LoggerInstance.debug("Enabling PayPal.");
		if (this.payPalActions) {
			this.payPalActions.enable();
			this.payPalEnabled = true;
		}
		this.payPalErrorMessages = null;
	}

	disablePayPal(errorMessages) {
		LoggerInstance.debug("Disabling PayPal.");
		if (this.payPalActions) {
			this.payPalActions.disable();
			this.payPalEnabled = false;
		}
		this.payPalErrorMessages = errorMessages;
	}

	getPayPalErrorMessages() {
		return this.payPalErrorMessages;
	}

	isPayPalEnabled() {
		return this.payPalEnabled;
	}

	updatePayPalButtonState() {
		var deferred = new $.Deferred();

		this.disablePayPal();

		if (window.CheckoutActions) {
			// We must use window.CheckoutActions to prevent a circular reference.
			return window.CheckoutActions.updatePayPalButtonState();
		} else {
			deferred.reject();
			return deferred;
		}
	}

	getConfirmationPageRoute() {
		var confirmationPageRoute = null;

		var $checkoutComponent = $("[data-component='createAccountAndCheckout'],[data-component='savedPayments']");

		if ($checkoutComponent && $checkoutComponent.length) {
			confirmationPageRoute = $checkoutComponent.attr("data-confirmation-page-route");
		}

		return confirmationPageRoute || "orderconfirmation";
	}

	getCheckoutDispatcher() {
		return Dispatcher;
	}
};

var CheckoutStoreSingleton = new CheckoutStore();

CheckoutStoreSingleton.dispatchToken = Dispatcher.register((action) => {
	var cartItem;
	switch (action.actionType) {
		case CheckoutConstants.CART_UPDATE_AMOUNT:
			cartItem = CheckoutStoreSingleton.getItem(action.cartItemId, action.itemType);
			if (cartItem) {
				cartItem.amount = action.amount;
				cartItem.isDirty = true;
			}
			break;

		case Constants.CART_UPDATE_CHARGE_TODAY:
			cartItem = CheckoutStoreSingleton.getItem(action.cartItemId, action.itemType);
			if (cartItem) {
				cartItem.customerChoseChargeToday = action.chargeToday;
				cartItem.isDirty = true;
			}
			break;

		case CheckoutConstants.CART_UPDATE_DONATION_AMOUNT_OPCO:
			if (!action.context) {
				state.donationAmount = action.selectedAmount;
				CheckoutStoreSingleton.updatePayPalButtonState();
				CheckoutStoreSingleton.setOPCOSubtotal();
			}
			break;

		case CheckoutConstants.CART_UPDATE_DONATION_CHARGE_TODAY_OPCO:
			if (!action.context) {
				state.isDonationChargeToday = action.isChargeToday;
				CheckoutStoreSingleton.setOPCOSubtotal();
			}
			break;

		case CheckoutConstants.CART_UPDATE_DONATION_FREQUENCY_OPCO:
			if (!action.context) {
				state.donationFrequency = action.donationFrequency;
			}
			break;

		case CheckoutConstants.SET_SUBACCOUNT_GIFT_AMOUNT_OPCO:
			state.subaccountAmounts[action.subaccountCode] = action.amount;
			CheckoutStoreSingleton.updatePayPalButtonState();
			CheckoutStoreSingleton.setOPCOSubtotal();
			break;

		case Constants.CART_UPDATE_FREQUENCY:
			cartItem = CheckoutStoreSingleton.getItem(action.cartItemId, action.itemType);
			if (cartItem) {
				if (action.frequency === "O") {
					cartItem.customerChoseChargeToday = false;
					cartItem.useOnDay = null;
					cartItem.isRecurring = false;
				} else if (cartItem.frequency === "O") {
					// Was one-time and now is not
					if (!cartItem.useOnDay && state.wizardDefaultUseOnDay && state.wizardDefaultUseOnDay.defaultUseOnDay) {
						cartItem.useOnDay = state.wizardDefaultUseOnDay.defaultUseOnDay;
						cartItem.isRecurring = true;
					}
				}

				if (action.frequency !== "O" && CheckoutStoreSingleton.getIsDonationChargeTodayRequired(action.cartItemId)) {
					cartItem.customerChoseChargeToday = true;
				}

				cartItem.frequency = action.frequency;
				cartItem.isDirty = true;
			}
			break;

		case Constants.CART_UPDATE_PRODUCT_OPCO_DISCOUNT:
			var updateSelectedShippingMethod = true;
			if (action.productDiscount > 0) {
				CheckoutStoreSingleton.setFreeShippingMethod(action.freeShippingMethod, action.freeShippingMethodDescription);
			} else {
				updateSelectedShippingMethod = CheckoutStoreSingleton.removeFreeShippingMethod();
			}

			state.productDiscount = action.productDiscount;
			state.freeShippingMethod = action.freeShippingMethod;
			state.freeShippingMethodDescription = action.freeShippingMethodDescription;

			if (updateSelectedShippingMethod) {
				CheckoutStoreSingleton.setShippingMethodRates();
			}

			CheckoutStoreSingleton.setShippingAmount();
			$("body").trigger("manuallyUpdateTax");
			CheckoutStoreSingleton.setOPCOSubtotal();
			break;

		case Constants.CART_UPDATE_PRODUCT_PRICE:
			state.productPrice = action.productPrice;
			CheckoutStoreSingleton.setOPCOSubtotal();
			break;

		case Constants.CART_UPDATE_QUANTITY:
			cartItem = CheckoutStoreSingleton.getItem(action.cartItemId, action.itemType);
			if (cartItem) {
				cartItem.quantity = action.quantity;
				cartItem.isDirty = true;
			}
			break;

		case Constants.CART_UPDATE_USE_ON_DAY:
			cartItem = CheckoutStoreSingleton.getItem(action.cartItemId, action.itemType);
			if (cartItem) {
				cartItem.useOnDay = action.useOnDay;
				cartItem.isDirty = true;
			}
			break;

		case CheckoutConstants.DISABLE_PAYPAL_CHECKOUT:
			CheckoutStoreSingleton.disablePayPal();
			break;

		case CheckoutConstants.ENABLE_PAYPAL_CHECKOUT:
			CheckoutStoreSingleton.enablePayPal();
			break;

		case CheckoutConstants.VALIDATE_PAYPAL_CHECKOUT:
			CheckoutStoreSingleton.updatePayPalButtonState();
			break;

		case Constants.DONATION_UPSELL_UPDATE:
			state.donationUpsellAmount = action.donationUpsellAmount;
			CheckoutStoreSingleton.setDonationUpsellAmount();
			CheckoutStoreSingleton.setRecurringDonationUpsellAmounts();

		case Constants.SHIPPING_REQUEST_PENDING:
			LoggerInstance.info("Getting Shipping Quote now.");
			break;

		case Constants.SHIPPING_UPDATED:
			LoggerInstance.debug(action.shippingRates);
			state.shippingRates = action.shippingRates;
			state.taxAmount = null; //The shippingAmount will potentially changed, so we would need to calculate tax again; setting it to null in case we do not have enough information to calculate
			CheckoutStoreSingleton.setShippingMethodRates();
			CheckoutStoreSingleton.setShippingAmount();
			CheckoutStoreSingleton.updatePayPalButtonState();
			CheckoutStoreSingleton.emitShippingEvent();
			return;

		case Constants.SHIPPING_CANNOT_BE_CALCULATED:
			LoggerInstance.debug("Shipping cannot be calculated yet.");
			state.shippingRates = null;
			state.taxAmount = null;
			CheckoutStoreSingleton.setShippingAmount();
			CheckoutStoreSingleton.emitShippingEvent();
			break;

		case Constants.SHIPPING_IS_ALL_FREE:
			LoggerInstance.debug("All shippable items have free shipping.");
			state.shippingRates = null;
			state.shippingMethod = null;
			state.taxAmount = null;

			$("[data-component='shippingMethods']").hide();

			CheckoutStoreSingleton.setShippingAmount();
			CheckoutStoreSingleton.emitShippingEvent();
			break;

		case Constants.SHIPPING_UPDATE_FAILED:
			LoggerInstance.error(action.jqXHR);
			state.shippingRates = null;
			state.taxAmount = null;
			CheckoutStoreSingleton.setShippingAmount();
			CheckoutStoreSingleton.emitShippingEvent();
			return;

		case Constants.SHIPPING_METHOD_UPDATED:
			state.shippingMethod = action.shippingMethod;
			state.taxAmount = null; //The shippingAmount will potentially changed, so we would need to calculate tax again; setting it to null in case we do not have enough information to calculate
			CheckoutStoreSingleton.setShippingAmount();
			CheckoutStoreSingleton.emitShippingEvent();
			return;

		case Constants.TAX_UPDATED:
			LoggerInstance.debug(action.taxAmount);
			state.taxAmount = action.taxAmount;
			CheckoutStoreSingleton.setTaxAmount();
			break;

		case Constants.TAX_UPDATE_FAILED:
			LoggerInstance.error(action.jqXHR);
			state.taxAmount = null;
			CheckoutStoreSingleton.setTaxAmount();
			break;

		case Constants.CHECKOUT_INITIALIZE:
			// Coalesce to null in case it is not passed
			state.allowedShippingMethods = action.shippingMethods;
			state.availableQuantity = (action.availableQuantity === 0 || action.availableQuantity) ? action.availableQuantity : null;
			state.donations = action.donations;
			state.eventRegistrations = action.eventRegistrations;
			state.products = action.products;
			state.subscriptions = action.subscriptions || []; //Defaulting here so we didn't have to add this to all the other tangible layouts.
			state.cartType = action.cartType || "CURRENT";
			state.cartHasDonationUpsell = action.cartHasDonationUpsell;
			state.checkoutType = action.checkoutType;
			state.errors = action.errors;
			state.errorsToPreventCheckoutExist = action.errorsToPreventCheckoutExist;
			state.isDesignMode = action.isDesignMode;
			state.isPayPalSupported = action.isPayPalSupported;
			state.isInPledgeStatementSession = action.isInPledgeStatementSession;
			state.isShoppingCart = action.isShoppingCart || false;
			state.saveForLaterDonations = action.saveForLaterDonations;
			state.saveForLaterRegistrations = action.saveForLaterRegistrations;
			state.saveForLaterProducts = action.saveForLaterProducts;
			state.saveForLaterSubscriptions = action.saveForLaterSubscriptions;
			state.wizardApplePayConfiguration = action.wizardApplePayConfiguration;
			state.wizardApplePayDisplayMessages = action.wizardApplePayDisplayMessages;
			state.wizardCheckoutDisplayMessages = action.wizardCheckoutDisplayMessages;
			state.wizardCompanyEftErrorMessages = action.wizardCompanyEftErrorMessages;
			state.wizardCompanyErrorMessages = action.wizardCompanyErrorMessages;
			state.wizardCouponDisplayMessages = action.wizardCouponDisplayMessages;
			state.wizardCreditCardDisplayMessages = action.wizardCreditCardDisplayMessages;
			state.wizardDefaultContactMethod = action.wizardDefaultContactMethod;
			state.wizardDefaultUseOnDay = action.wizardDefaultUseOnDay;
			state.wizardFrequencies = action.wizardFrequencies;
			state.wizardGooglePayConfiguration = action.wizardGooglePayConfiguration;
			state.wizardGooglePayDisplayMessages = action.wizardGooglePayDisplayMessages;
			state.wizardUseOnDays = action.wizardUseOnDays;
			state.wizardDonationRequired = action.wizardDonationRequired;
			state.wizardDonationUpsell = action.wizardDonationUpsell;
			state.wizardOneTimePasswordConfiguration = action.wizardOneTimePasswordConfiguration;
			state.wizardOneTimePasswordForGuestCheckoutMessages = action.wizardOneTimePasswordForGuestCheckoutMessages;
			state.wizardPayPalDisplayMessages = action.wizardPayPalDisplayMessages;
			state.wizardProjectCompletionDefaults = action.wizardProjectCompletionDefaults || {};
			state.wizardRecurringRequired = action.wizardRecurringRequired;
			state.wizardSavedPaymentsDisplayMessages = action.wizardSavedPaymentsDisplayMessages;
			state.wizardShippingDisplayMessages = action.wizardShippingDisplayMessages;

			action.wizardCompany = action.wizardCompany || {};
			state.company = action.wizardCompany.company;
			state.currency = action.wizardCompany.currency;
			state.warehouse = action.wizardCompany.warehouse;

			state.loggedInAccount = action.loggedInAccount;
			state.shippingAddress = action.shippingAddress;
			state.productCode = action.productCode;
			state.productPrice = action.productPrice;

			state.defaultShippingMethod = action.defaultShippingMethod;
			state.freeShippingMethod = action.freeShippingMethod;
			state.hasShippableProducts = action.hasShippableProducts;
			state.hasOnlyDownloadableOrFreeShippingProducts = action.hasOnlyDownloadableOrFreeShippingProducts;

			state.defaultSubtotalLabel = $("[data-dynamic='Subtotal']").html();
			state.defaultShippingLabel = $("[data-dynamic='Shipping']").html();
			state.defaultTaxLabel = $("[data-dynamic='Tax']").html();
			state.defaultTotalLabel = $("[data-dynamic='Total']").html();
			state.defaultDonationUpsellAmountLabel = $("[data-dynamic='DonationUpsell']").html();

			state.donationSubtotal = action.donationSubtotal;
			state.eventRegistrationSubtotal = action.eventRegistrationSubtotal;
			state.productSubtotal = action.productSubtotal;
			state.subscriptionSubtotal = action.subscriptionSubtotal;

			CheckoutStoreSingleton.setOPCOSubtotal();
			CheckoutStoreSingleton.initializeShippingMethodRates();
			CheckoutStoreSingleton.initializeTangibleChangeHandler();
			CheckoutStoreSingleton.updatePaymentComponents();

			return true; // Don't emit change

		default:
			// no op
			return true;
	}

	CheckoutStoreSingleton.emitChange();
	return true;
});

module.exports = CheckoutStoreSingleton;
